import Constants from '@/Constants';
import { RadialChartDataType } from '@/models/RadialChart';
import { ApplicationState } from '@/store';
import { fade } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RadialChart } from 'react-vis';
import './style.scss';

interface Props {
    chartType: string;
    completedItems?: number;
    totalItems?: number;
    itemType?: string;
    progressBarColor?: string;
    radius: number;
}

const RadialChartComponent: React.FC<Props> = (props: Props) => {
    const { strings, colors } = useSelector((state: ApplicationState) => ({
        strings: state.locale.data,
        colors: state.appOrganization.data.colors,
    }));
    const { chartType, completedItems, totalItems, itemType, progressBarColor, radius } = props;
    const primaryColor = colors[0];

    const [containerHeight, setContainerHeight] = useState<number>(0);

    const calculatePerc = (isRemainingItems?: boolean) => {
        if (typeof totalItems === 'number' && typeof completedItems === 'number' && totalItems > 0) {
            if (chartType === Constants.radialChartTypes.GOAL_CHART) {
                return isRemainingItems ? totalItems - completedItems / 2 : completedItems / 2;
            }
            return isRemainingItems ? totalItems - completedItems : completedItems;
        }
        if (chartType === Constants.radialChartTypes.LEARNING_MAP_CHART) {
            return isRemainingItems ? 75 : 25;
        }
        return 50;
    };

    const chartData: RadialChartDataType[] = [
        { angle: calculatePerc(), color: progressBarColor || primaryColor },
        { angle: calculatePerc(true), color: fade(progressBarColor || primaryColor, 0.1) },
    ];

    const handleHeightContainer = (radius: number): number => (chartType === Constants.radialChartTypes.MEDIA_CHART ? radius * 2 : radius);

    const handleClassName = (): string => (chartType === Constants.radialChartTypes.MEDIA_CHART ? 'chartContainer' : 'halfChartContainer');

    const handleInnerRadius = (isInnerLabel?: boolean): number => {
        switch (chartType) {
            case Constants.radialChartTypes.LEARNING_MAP_CHART:
                return radius * 0.5;
            case Constants.radialChartTypes.MEDIA_CHART:
                return radius * 0.78;
            default:
                return isInnerLabel ? radius : radius * 0.65;
        }
    };

    const handleRotation = (): string => (chartType === Constants.radialChartTypes.MEDIA_CHART ? 'rotate(0deg)' : 'rotate(-90deg)');

    const handleInnerLabel = (): string => (chartType === Constants.radialChartTypes.GOAL_CHART ? 'goalChartInnerLabel' : 'innerLabel');

    const renderGoalChartInnerLabel = () => (
        <>
            <b>{completedItems || 0}</b>
            <p>{`/${totalItems || 0}${strings.MINUTES}`}</p>
        </>
    );

    const renderInnerLabel = () => (
        <>
            <b>{completedItems || 0}</b>
            <p>{completedItems && completedItems === 1 ? strings.ITEM : strings.ITEMS}</p>
        </>
    );

    const handleInnerLabelText = () => {
        switch (chartType) {
            case Constants.radialChartTypes.GOAL_CHART:
                return renderGoalChartInnerLabel();
            default:
                return renderInnerLabel();
        }
    };

    useEffect(() => {
        setContainerHeight(handleHeightContainer(radius));
    }, [chartType, radius]);

    return (
        <>
            {containerHeight > 0 && (
                <div className="radialChart" style={{ width: radius * 2 }}>
                    <div
                        className={handleClassName()}
                        style={{
                            height: containerHeight,
                            width: radius * 2,
                        }}
                    >
                        <RadialChart
                            colorType="literal"
                            data={chartData}
                            height={radius * 2}
                            innerRadius={handleInnerRadius()}
                            radius={radius}
                            style={{ transform: handleRotation() }}
                            width={radius * 2}
                        />
                        {chartType !== Constants.radialChartTypes.LEARNING_MAP_CHART && (
                            <div
                                className={handleInnerLabel()}
                                style={{
                                    width: radius * 1.5,
                                    height: handleInnerRadius(true),
                                }}
                            >
                                {handleInnerLabelText()}
                            </div>
                        )}
                    </div>
                    {chartType === Constants.radialChartTypes.MEDIA_CHART && (
                        <div className="label">
                            <p>{itemType}</p>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default RadialChartComponent;
